// src/components/LogisticaModule.js
import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Html5QrcodeScanner } from 'html5-qrcode';

function LogisticaModule() {
  const { token, user } = useAuth(); 
  const [scannedGuide, setScannedGuide] = useState('');
  const [scannedItems, setScannedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  const inputRef = useRef(null);
  const formRef = useRef(null);
  const [carrierInCurrentEmbarkment, setCarrierInCurrentEmbarkment] = useState(null);
  
  const [showCameraScanner, setShowCameraScanner] = useState(false);
  const [selectedStatusToUpdate, setSelectedStatusToUpdate] = useState(''); // Nuevo estado para el selector de estatus

  // useEffect para asegurar el foco inicial en el input
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []); // Se ejecuta solo una vez al montar

  // Nuevo useEffect para asegurar el foco después de cada escaneo/búsqueda exitosa
  useEffect(() => {
    // Solo intenta enfocar si no estamos cargando y el campo de guía está vacío
    // Esto significa que la operación anterior terminó y el campo fue limpiado
    if (!loading && scannedGuide === '' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [loading, scannedGuide]); // Depende de 'loading' y 'scannedGuide'

  useEffect(() => {
    if (!showCameraScanner) {
      return;
    }

    const scanner = new Html5QrcodeScanner(
      'camera-reader',
      {
        qrbox: {
          width: 300,
          height: 200,
        },
        fps: 10,
      },
      false
    );

    let isScanning = true;

    function onScanSuccess(decodedText, decodedResult) {
      if (isScanning) {
        isScanning = false;
        scanner.clear();
        setShowCameraScanner(false);
        // Lógica de recorte simplificada: si tiene 34 dígitos, toma los últimos 12.
        let processedGuide = decodedText;
        if (decodedText.length === 34) {
            processedGuide = decodedText.substring(decodedText.length - 12);
            console.log(`DEBUG: Guía de 34 dígitos escaneada y recortada a: ${processedGuide}`);
        }
        setScannedGuide(processedGuide);
        // Dispara la búsqueda automáticamente después de escanear
        if (formRef.current) {
          formRef.current.requestSubmit();
        }
      }
    }

    function onScanError(error) {
      // console.warn(error); // Puedes dejar esto para depuración o quitarlo si es muy ruidoso
    }

    scanner.render(onScanSuccess, onScanError);

    // Función de limpieza para detener la cámara si el componente se desmonta o el escáner se oculta
    return () => {
        if (scanner && scanner.getState() === 2) { // Html5Qrcode.Html5QrcodeScannerState.SCANNING = 2
            scanner.clear().catch(err => console.error("Fallo al limpiar el escáner", err));
        }
    };
  }, [showCameraScanner]);


  const audioSuccess = new Audio('/sounds/success.mp3');
  const audioError = new Audio('/sounds/error.mp3');

  const formatMercadoLibreGuide = (guideNumber) => {
    if (!guideNumber) return 'N/A';
    const match = guideNumber.match(/MEL(\d+)(?:LMXDF01|FMDOF01)/);
    if (match && match[1]) return match[1];
    return guideNumber;
  };

  const abbreviatePaqueteria = (trackingMethod) => {
    if (!trackingMethod) return 'N/A';
    const lowerCaseMethod = trackingMethod.toLowerCase();
    if (lowerCaseMethod.includes('fedex')) return 'FedEx';
    if (lowerCaseMethod.includes('paquetexpress')) return 'PaqExp';
    if (lowerCaseMethod.includes('mel') || lowerCaseMethod.includes('mercado libre')) return 'ML';
    return trackingMethod.split(' ')[0];
  };

  const handleScan = async (e) => {
    if(e) e.preventDefault(); // Previene la recarga de la página al enviar el formulario
    let currentGuide = scannedGuide.trim();

    // Lógica de recorte para entrada manual o si el escáner no lo recortó
    if (currentGuide.length === 34) {
        currentGuide = currentGuide.substring(currentGuide.length - 12);
        console.log(`DEBUG: Guía de 34 dígitos (manual/no escáner) recortada a: ${currentGuide}`);
        setScannedGuide(currentGuide); // Actualiza el estado para reflejar el recorte
    }

    if (!currentGuide) {
      setError('Por favor, ingresa o escanea un número de guía.');
      return;
    }
    setLoading(true);
    setError(null);
    setMessage('');

    try {
      // --- Lógica para actualizar estado a "Preparación" directamente ---
      if (selectedStatusToUpdate && selectedStatusToUpdate !== 'Enviada') {
        console.log(`DEBUG: Intentando actualizar estado de guía ${currentGuide} a ${selectedStatusToUpdate}`);
  const statusUpdateResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/orders/update-status-by-guide`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            guideNumber: currentGuide,
            newStatus: selectedStatusToUpdate,
            performed_by_user_id: user?.id || null,
            performed_by_username: user?.nombre || 'Desconocido'
          })
        });

        const statusUpdateData = await statusUpdateResponse.json();

        if (statusUpdateResponse.ok) {
          setMessage(`Guía ${currentGuide} actualizada a "${selectedStatusToUpdate}" exitosamente.`);
          audioSuccess.play();
          setScannedGuide(''); 
          setLoading(false);
          if(inputRef.current) inputRef.current.focus();
          return; 
        } else {
          setError(`Error al actualizar estado de guía ${currentGuide}: ${statusUpdateData.mensaje || 'Error desconocido'}`);
          audioError.play();
          setScannedGuide(''); 
          setLoading(false);
          if(inputRef.current) inputRef.current.focus();
          return;
        }
      }

      // --- Lógica para añadir a embarque (si es "Enviada" o no se seleccionó estado) ---
      console.log(`DEBUG: Enviando al backend para búsqueda (embarque): /api/orders/guide-details?guideNumber=${currentGuide}`);
      const response = await fetch(`/api/orders/guide-details?guideNumber=${currentGuide}`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await response.json();
      console.log('DEBUG: Respuesta del backend para guía (embarque):', data);

      if (response.ok && data.order) {
        const scannedPaqueteria = abbreviatePaqueteria(data.order.raw_data?.Shipment?.TrackingMethod);
        if (scannedItems.length === 0) setCarrierInCurrentEmbarkment(scannedPaqueteria);
        else if (carrierInCurrentEmbarkment && scannedPaqueteria !== carrierInCurrentEmbarkment) {
          setError(`Solo guías de ${carrierInCurrentEmbarkment}. Limpia el embarque para uno nuevo.`);
          audioError.play();
          setLoading(false);
          setScannedGuide('');
          if(inputRef.current) inputRef.current.focus();
          return;
        }
        let alreadyScanned = false;
        setScannedItems(prevItems => {
          if (prevItems.some(item => item.guide_number === currentGuide)) {
            setError('Esta guía ya ha sido escaneada para este embarque.');
            audioError.play();
            alreadyScanned = true;
            return prevItems;
          }
          const descripcionLlantas = data.order.raw_data?.Items?.map(item => `${item.Quantity}x ${item.Titulo} (SKU: ${item.Sku})`).join('; ') || 'N/A';
          const totalLlantas = data.order.total_llantas;
          return [...prevItems, {
            order_id: data.order.id, guide_number: currentGuide, referencia_plataforma: data.order.referencia_plataforma,
            folio_autoazur: data.order.folio_autoazur, descripcion: descripcionLlantas, cantidad: totalLlantas,
            paqueteria: scannedPaqueteria, canal_origen: data.order.canal_origen
          }];
        });
        if (!alreadyScanned) {
            setMessage(`Guía ${currentGuide} añadida al embarque para impresión.`);
            audioSuccess.play();
        }
      } else {
        setError(data.mensaje || 'Guía no encontrada en el sistema para embarque.');
        audioError.play();
      }
    } catch (err) {
      setError('Error de conexión con el servidor.');
      audioError.play();
    } finally {
      setLoading(false); 
      setScannedGuide(''); 
      if(inputRef.current) inputRef.current.focus();
    }
  };

  const handlePrintEmbarkment = () => {
    if (scannedItems.length === 0) {
      setMessage('No hay guías escaneadas para imprimir el embarque.');
      return;
    }

    const today = new Date();
    const formattedDate = `${today.getDate().toString().padStart(2, '0')}/${(today.getMonth() + 1).toString().padStart(2, '0')}/${today.getFullYear()} ${today.getHours().toString().padStart(2, '0')}:${today.getMinutes().toString().padStart(2, '0')}`;
    let embarkmentTitle = `Embarque ${carrierInCurrentEmbarkment || 'General'}`;

    let tableRows = '';
    scannedItems.forEach(item => {
      tableRows += `
        <tr>
          <td style="border: 1px solid #ccc; padding: 8px; text-align: left;">${item.guide_number}</td>
          <td style="border: 1px solid #ccc; padding: 8px; text-align: left;">${item.descripcion}</td>
          <td style="border: 1px solid #ccc; padding: 8px; text-align: center;">${item.cantidad}</td>
          <td style="border: 1px solid #ccc; padding: 8px; text-align: center;">${item.paqueteria}</td>
        </tr>
      `;
    });

    const printContent = `
      <html>
        <head>
          <title>Embarque de Salida</title>
          <style>
            body { font-family: sans-serif; margin: 20px; }
            .header-info { display: flex; justify-content: space-between; margin-bottom: 20px; }
            .header-info div { flex: 1; text-align: center; }
            .header-info div:nth-child(1) { text-align: left; }
            .header-info div:nth-child(3) { text-align: right; }
            .main-title { font-size: 24px; font-weight: bold; text-align: center; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { border: 1px solid #ccc; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .footer-total { text-align: right; font-weight: bold; }
            @media print {
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header-info">
            <div></div>
            <div></div>
            <div>${formattedDate}</div>
          </div>
          <div class="header-info">
            <div></div>
            <div>${embarkmentTitle}</div>
            <div></div>
          </div>
          <table>
            <thead>
              <tr>
                <th style="width: 25%;">Guía</th>
                <th style="width: 40%;">Descripción</th>
                <th style="width: 15%;">Cantidad</th>
                <th style="width: 20%;">Paquetería</th>
              </tr>
                </thead>
                <tbody>
                  ${tableRows}
                </tbody>
              </table>
              <div class="footer-total">
                Total de paquetes: ${scannedItems.length}
              </div>
            </body>
          </html>
        `;

        const printWindow = window.open('', '_blank');
        if (printWindow) {
          printWindow.document.write(printContent);
          printWindow.document.close();
          printWindow.focus();
          printWindow.onload = () => {
            printWindow.print();
            setTimeout(() => {
              // Reemplazado alert con un mensaje más amigable o un modal personalizado si fuera necesario
              if (window.confirm(`¿Deseas marcar los ${scannedItems.length} pedidos de este embarque como "Enviada"?`)) {
                handleBulkUpdateStatus();
              }
            }, 500);
          };
        } else {
          // Reemplazado alert con un mensaje más amigable
          setMessage('El navegador bloqueó la ventana de impresión. Por favor, permite pop-ups para este sitio.');
        }
      };
      
      const handleBulkUpdateStatus = async () => {
        setLoading(true);
        setMessage('Actualizando estados a "Enviada"...');
        setError(null);
        const orderIds = scannedItems.map(item => item.order_id);
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/orders/bulk-update-status`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              orderIds: orderIds,
              newStatus: 'Enviada'
            })
          });
          const data = await response.json();
          if (response.ok) {
            setMessage(data.mensaje);
            handleClearEmbarkment();
          } else {
            setError(data.mensaje || 'Error al actualizar los pedidos.');
          }
        } catch (err) {
          setError('Error de conexión con el servidor.');
        } finally {
          setLoading(false);
        }
      };
      
      const handleClearEmbarkment = () => {
        setScannedItems([]);
        setCarrierInCurrentEmbarkment(null);
        setError(null);
        setMessage('');
        setScannedGuide('');
        if(inputRef.current) inputRef.current.focus();
      };

      return (
        <>
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Módulo de Cuadre de Guías (Logística)</h2>
          <div className="bg-white p-6 rounded-lg shadow mb-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Escanear / Ingresar Guía y Actualizar Estado</h3>
            
            {/* Selector de Estatus */}
            <div className="mb-4">
                <label htmlFor="status-selector" className="block text-gray-700 text-sm font-bold mb-2">
                    Cambiar estado a:
                </label>
                <select
                    id="status-selector"
                    className="shadow appearance-none border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    value={selectedStatusToUpdate}
                    onChange={(e) => setSelectedStatusToUpdate(e.target.value)}
                >
                    <option value="">(No cambiar estado)</option>
                    <option value="Preparación">Preparación</option>
                    <option value="Enviada">Enviada</option>
                </select>
                <p className="text-sm text-gray-500 mt-2">
                    Selecciona un estado para actualizar el pedido de la guía escaneada. Si no seleccionas, la guía se añadirá al embarque.
                </p>
            </div>

            {/* --- Contenedor para la cámara --- */}
            {showCameraScanner && <div id="camera-reader" className="w-full md:w-1/2 mx-auto my-4 border rounded-lg"></div>}

            <form ref={formRef} onSubmit={handleScan} className="flex flex-col sm:flex-row items-center sm:space-x-4 mb-4">
              <input
                type="text"
                ref={inputRef}
                className="shadow appearance-none border rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:shadow-outline flex-grow mb-2 sm:mb-0"
                placeholder="Esperando escaneo de guía..."
                value={scannedGuide}
                onChange={(e) => setScannedGuide(e.target.value)}
                disabled={loading}
              />
              <div className="flex w-full sm:w-auto space-x-2">
                <button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg" disabled={loading}>
                  {loading ? '...' : 'Buscar / Actualizar'}
                </button>
                {/* --- Botón para activar la cámara --- */}
                <button type="button" onClick={() => setShowCameraScanner(!showCameraScanner)} className="bg-gray-700 hover:bg-gray-800 text-white font-bold p-3 rounded-lg" title="Escanear con Cámara">
                  📸
                </button>
              </div>
            </form>

            {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-4">{error}</div>}
            {message && <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg mb-4">{message}</div>}

            <h3 className="text-xl font-semibold text-gray-800 mb-4 mt-6">
              Guías Escaneadas para Embarque
              {carrierInCurrentEmbarkment && <span className="ml-3 text-blue-600 text-base font-medium">(Paquetería: {carrierInCurrentEmbarkment})</span>}
            </h3>
            {scannedItems.length === 0 ? (
              <p className="text-gray-600">No hay guías escaneadas.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Guía</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Folio AZ</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descripción</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cantidad</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paquetería</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {scannedItems.map((item, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.canal_origen === 'MERCADO LIBRE' ? formatMercadoLibreGuide(item.guide_number) : item.guide_number}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{item.folio_autoazur}</td>
                        <td className="px-6 py-4 text-sm text-gray-700">{item.descripcion}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{item.cantidad}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{item.paqueteria}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {scannedItems.length > 0 && (
              <div className="mt-6 flex justify-end space-x-4">
                <button onClick={handleClearEmbarkment} className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg">Limpiar Embarque</button>
                <button onClick={handlePrintEmbarkment} className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg">Imprimir Embarque y Finalizar</button>
              </div>
            )}
          </div>
        </>
      );
    }

    export default LogisticaModule;
